import { useCallback, useEffect, useState } from "react";

import useMountedRef from "./useMounted";
import {
  clearShipWithMemberCache,
  fetchShipWithMembers,
  isLinked,
  shipWithCacheKey,
} from "./shipWith";

const EMPTY = [];

/**
 * The members of a linked shipment's group — siblings and self — for the
 * asides and the UNLINK button.
 *
 * Fetches when `isLinked(record)` and again whenever the group changes; the
 * join is on the sorted id string, so a re-render that hands over a fresh array
 * of the same ids does not refetch. A singleton gets `members: []` and no
 * request. `refresh()` refetches and also drops the badge's cached entry for
 * these ids, so a tooltip opened after an unlink shows the new group.
 */
export default function useShipWithMembers(record) {
  const key = isLinked(record) ? shipWithCacheKey(record.shipWithIds) : "";
  const [loaded, setLoaded] = useState({ key: "", members: EMPTY });
  const [tick, setTick] = useState(0);
  const mounted = useMountedRef();

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    fetchShipWithMembers(key.split(",")).then((members) => {
      if (cancelled || !mounted.current) return;
      setLoaded({ key, members });
    });
    return () => {
      cancelled = true;
    };
    // `tick` is the refresh trigger; it carries no value of its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);

  const refresh = useCallback(() => {
    if (!key) return;
    clearShipWithMemberCache(key.split(","));
    setTick((t) => t + 1);
  }, [key]);

  // Derive from the key rather than a loading flag so a group change never
  // shows the previous group's members for a render.
  const current = key && loaded.key === key;
  return {
    members: current ? loaded.members : EMPTY,
    loading: Boolean(key) && !current,
    refresh,
  };
}
